import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { businesses as bizApi } from '../services/api';
import { Search, Ticket, Star, MapPin, Loader2, Building2, Radio } from 'lucide-react';
import ClampWithMore from '../components/ClampWithMore';
import AppImage from '../components/AppImage';
import BusinessLogoAvatar from '../components/BusinessLogoAvatar';
import { PBB_CATEGORIES } from '../components/CategoryChips';
import SiteSections from '../components/SiteSections';

// Re-export the master list under the local name for downstream code.
const CATEGORIES = PBB_CATEGORIES;

// Category-specific hero cards shown when a user picks a category that has
// no live listings yet. Keeps freshly added categories from looking dead
// and doubles as an implicit "list your business here" cue.
const CATEGORY_HEROES = {
  Media: {
    image: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=1200&auto=format&fit=crop&q=80',
    icon: Radio,
    title: 'The Media category is live — be the first listed',
    blurb: 'Photographers, podcasters, videographers, filmmakers, journalists, radio hosts, content creators, and production studios all belong here. Claim founding-member status while spots are open.',
  },
};

export default function Directory() {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    bizApi.list().then(r => setItems(r)).finally(() => setLoading(false));
  }, []);

  const list = useMemo(() => items.filter(b =>
    (cat === 'All' || b.category === cat) &&
    (!q || b.name.toLowerCase().includes(q.toLowerCase()) || b.city.toLowerCase().includes(q.toLowerCase()))
  ), [q, cat, items]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Business Directory</h1>
        <p className="text-gray-400 text-sm">Discover Black-owned businesses. Every visit earns you points.</p>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
        <input className="pbb-input pl-10" placeholder="Search businesses, cities, categories..."
          value={q} onChange={e => setQ(e.target.value)} />
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <button className={`pbb-chip ${cat==='All'?'active':''}`} onClick={()=>setCat('All')}>All</button>
        {CATEGORIES.map(c => (
          <button key={c} className={`pbb-chip ${cat===c?'active':''}`} onClick={()=>setCat(c)}>{c}</button>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>
      ) : items.length === 0 ? (
        <div className="pbb-card border-2 border-dashed border-green-500/30 bg-green-500/5 p-10 text-center" data-testid="directory-empty-state">
          <Building2 className="mx-auto text-green-400" size={30} />
          <h3 className="mt-4 text-xl font-bold">Be one of the first 100 businesses listed</h3>
          <p className="mt-2 text-sm text-gray-300 max-w-md mx-auto">Register now and get <span className="font-semibold text-green-400">founding member status</span> — permanent recognition, priority placement, and early access to loyalty tools when the directory opens.</p>
          <Link to="/signup" data-testid="directory-empty-cta" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-semibold">Claim Founding Spot</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(b => (
            <Link key={b.id} to={`/business/${b.id}`} className="pbb-card overflow-hidden flex flex-col h-full">
              <div className="relative">
                <AppImage src={b.image} alt={b.name} rounded="none" />
                <div className="absolute -bottom-6 left-4 z-10">
                  <BusinessLogoAvatar business={b} size="md" />
                </div>
              </div>
              <div className="p-4 pt-8 flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <div className="font-semibold pbb-card-title" title={b.name}>{b.name}</div>
                  <div className="flex items-center gap-1 text-xs text-green-400"><Star size={12} className="fill-green-400"/>{b.rating}</div>
                </div>
                <div className="text-xs text-gray-400 mt-1 flex items-center gap-1"><MapPin size={12}/>{[b.city, b.state].filter(Boolean).join(', ')} • {b.category}</div>
                <ClampWithMore text={b.description} lines={3} className="text-xs text-gray-400 mt-2" testId={`biz-desc-${b.id}`} />
                <div className="flex items-center justify-between mt-3">
                  <div className="inline-flex items-center gap-2 px-2 py-1 rounded bg-green-600/10 border border-green-600/30 text-green-400 text-xs"><Ticket size={12}/> {b.coupon}</div>
                  <div className="text-xs text-gray-400">+{b.points} pts</div>
                </div>
              </div>
            </Link>
          ))}
          {list.length === 0 && (() => {
            const hero = CATEGORY_HEROES[cat];
            if (!hero) {
              return <div className="text-gray-500 text-sm col-span-full">No businesses match your filters.</div>;
            }
            const HeroIcon = hero.icon;
            return (
              <div
                data-testid={`category-hero-${cat.toLowerCase()}`}
                className="col-span-full pbb-card overflow-hidden grid grid-cols-1 md:grid-cols-2"
              >
                <div className="relative min-h-[220px]">
                  <AppImage src={hero.image} alt={cat} rounded="none" />
                </div>
                <div className="p-6 sm:p-8 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-green-400 font-semibold">
                    <HeroIcon size={14} /> {cat}
                  </div>
                  <h3 className="mt-2 text-2xl font-bold text-white">{hero.title}</h3>
                  <p className="mt-2 text-sm text-gray-300 leading-relaxed">{hero.blurb}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    <Link
                      to="/signup"
                      data-testid={`category-hero-${cat.toLowerCase()}-cta`}
                      className="inline-flex items-center gap-1 text-sm px-5 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-black font-semibold"
                    >
                      List your business
                    </Link>
                    <button
                      onClick={() => setCat('All')}
                      data-testid={`category-hero-${cat.toLowerCase()}-browse`}
                      className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-gray-300"
                    >
                      Browse all businesses
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
      <SiteSections pageKey="directory" />
    </div>
  );
}
